// stores/myPage.ts - 我的页面 Pinia Store
// Phase3 集成计划（不改变现有模拟行为，添加集成占位与样板调用）：
// - listRecords: 从后端分页拉取翻译记录（支持 lastRecordId/pageSize）
// - 任务操作：取消任务、重新翻译
// - 会员与配额：对接 getLimitInfo/统计/消耗记录/套餐历史 真实接口

import { defineStore } from 'pinia'
import { useTranslationApi } from '~/composables/useTranslationApi'
import { TranslateStatus } from '~/types/translation'
import type { Int64Id } from '~/types/translation'
import { normalizeInt64IdOrThrow } from '~/utils/int64Id.js'


// ==================== 类型定义 ====================

// 会员信息
export interface MembershipInfo {
  level: 'free' | 'vip'
  remainingPages: number        // 剩余总页数
  permanentPages: number         // 永久页数
  temporaryPages: number         // 临时页数
  nearestExpireDate: string | null  // 最近过期日期(ISO格式)
  dailyFreeLimit: number         // 每日免费限额
  dailyUsed: number             // 今日已用次数
  extraPackages: number         // 额外加油包页数
  vipExpireDate?: string | null  // 会员有效期（ISO），来自 usermix
}

// 翻译任务
export interface TranslationTask {
  id: string
  originalFileName: string
  fileSize: string
  sourceLanguage?: string        // 源语言(可选)
  targetLanguage: string
  status: 'uploading' | 'translating' | 'completed' | 'failed'
  progress?: number             // 0-100
  translatedPages?: number      // 已翻译页数
  consumedPages?: number        // 消耗页数
  createdAt: string             // ISO格式时间
  completedAt?: string          // 完成时间
  downloadUrl?: string
  error?: string                // 失败原因
}

// 统计信息
export interface Statistics {
  totalTranslations: number
  monthlyTranslations: number
  totalFileSize: string
}

// 消耗记录
export interface ConsumptionRecord {
  id: string
  type: 'docTranslate' | 'docTranslateFail' | 'vipIncrease' | 'adminOpt'
  fileName?: string             // 文档翻译相关
  increaseTotalPages: number    // 页数变化(正数增加,负数减少)
  createdAt: string
  metadata?: {
    targetLanguage?: string
    expireTime?: string
    reason?: string
  }
}

// 套餐历史
export interface PurchaseHistory {
  id: string
  packageName: string
  pages: number
  purchasedAt: string
  status: 'active' | 'expired'
  expiresAt: string | null
}

// ==================== Store定义 ====================

export const useMyPageStore = defineStore('myPage', {
  state: () => ({
    // 会员信息
    membershipInfo: {
      level: 'vip' as 'free' | 'vip',
      remainingPages: 1250,
      permanentPages: 750,
      temporaryPages: 500,
      nearestExpireDate: '2025-11-08' as string | null,
      dailyFreeLimit: 2,
      dailyUsed: 0,
      extraPackages: 0,
      vipExpireDate: null as string | null
    },

    // 会员信息加载状态（用于避免渲染抖动）
    membershipLoading: false,
    membershipLoaded: false,

    // 翻译记录
    allTasks: [] as TranslationTask[],
    currentTab: 'all' as 'all' | 'translating' | 'completed' | 'failed',

    // 历史记录元信息：用于“复用 fileId 重新翻译”
    // key: recordId(string)
    recordMetaById: {} as Record<string, { srcFileId: Int64Id; from: string; to: string; srcFileName: string }>,

    // 统计信息
    statistics: {
      totalTranslations: 125,
      monthlyTranslations: 18,
      totalFileSize: '2.3 GB'
    },
    // 统计加载状态（用于骨架屏显示控制）
    statisticsLoading: true,
    statisticsLoaded: false,

    // 消耗历史
    consumptionRecords: [] as ConsumptionRecord[],

    // 套餐历史
    purchaseHistory: [] as PurchaseHistory[]
  }),

  getters: {
    // 会员等级显示名称
    membershipLevelName: (state): string => {
      const levels = {
        free: '普通用户',
        vip: 'VIP会员'
      }
      return levels[state.membershipInfo.level]
    },

    // 是否有即将过期的页数
    hasExpiringPages: (state): boolean => {
      return state.membershipInfo.temporaryPages > 0
    },

    // 根据当前Tab过滤任务列表
    filteredTasks: (state): TranslationTask[] => {
      if (state.currentTab === 'all') {
        // 不显示失败的任务
        return state.allTasks.filter(task => task.status !== 'failed')
      }
      if (state.currentTab === 'translating') {
        return state.allTasks.filter(task =>
          task.status === 'uploading' || task.status === 'translating'
        )
      }
      return state.allTasks.filter(task => task.status === state.currentTab)
    },

    // 各Tab的任务数量
    taskCounts: (state) => {
      return {
        all: state.allTasks.filter(t => t.status !== 'failed').length,
        translating: state.allTasks.filter(t =>
          t.status === 'uploading' || t.status === 'translating'
        ).length,
        completed: state.allTasks.filter(t => t.status === 'completed').length,
        failed: state.allTasks.filter(t => t.status === 'failed').length
      }
    }
  },

  actions: {
    // ==================== 会员信息相关 ====================

    async fetchMembershipInfo() {
      this.membershipLoading = true
      const { getLimitInfo } = useTranslationApi()
      let info: { totalPages: number; pageList: Array<{ pages: number; expireTime: string }> } = {
        totalPages: 0,
        pageList: []
      }
      try {
        info = await getLimitInfo()
      } finally {
        // 无论成功与否都结束 loading，错误提示由全局拦截器处理
        this.membershipLoading = false
        this.membershipLoaded = true
      }

      // 以“是否存在过期时间(>0)”区分临时与永久页数
      const tmpItems = (info.pageList || []).filter(p => {
        const ts = Date.parse(p.expireTime)
        return Number.isFinite(ts) && ts > 0
      })
      const temporaryPages = tmpItems.reduce((s, x) => s + (x.pages || 0), 0)
      const permanentPages = Math.max(0, (info.totalPages || 0) - temporaryPages)
      const validTimestamps = tmpItems
        .map(x => Date.parse(x.expireTime))
        .filter(v => Number.isFinite(v) && v > 0)
        .sort((a, b) => a - b)
      const nearestExpireDate = validTimestamps.length > 0
        ? new Date(validTimestamps[0]!).toISOString().slice(0, 10)
        : null

      this.membershipInfo = {
        ...this.membershipInfo,
        remainingPages: info.totalPages || 0,
        permanentPages,
        temporaryPages,
        nearestExpireDate
      }
    },

    /**
     * 从 usermix 聚合数据同步会员与配额（Sidebar 与 Header 使用）
     * - 作为单一来源：不再在此分支调用 limit_info
     */
    syncMembershipFromMix(doc: { totalPages: number; pageList: Array<{ pages: number; expireTs: number }>; expireTs?: number }, vipList: Array<{ vipType: string; expireTs: number }>) {
      this.membershipLoading = true
      try {
        const total = Number(doc?.totalPages || 0)
        const tmpItems = (doc?.pageList || []).filter(p => Number(p?.expireTs || 0) > 0)
        const temporaryPages = tmpItems.reduce((s, x) => s + Number(x.pages || 0), 0)
        const permanentPages = Math.max(0, total - temporaryPages)
        const validTimestamps = tmpItems
          .map(x => Number(x.expireTs || 0))
          .filter(v => Number.isFinite(v) && v > 0)
          .sort((a, b) => a - b)
        const nearestExpireDate = (() => {
          if (validTimestamps.length > 0) {
            const firstTs = validTimestamps.at(0)
            if (typeof firstTs === 'number' && Number.isFinite(firstTs)) {
              return new Date(firstTs * 1000).toISOString().slice(0, 10)
            }
          }
          return null
        })()

        // 会员等级：参考移动端，存在 translate_vip 且未过期
        const nowSec = Math.floor(Date.now() / 1000)
        const vipItem = (vipList || []).find(v => v?.vipType === 'translate_vip')
        const isVip = !!vipItem && Number(vipItem?.expireTs || 0) > nowSec
        const vipExpireDate = vipItem && Number(vipItem?.expireTs || 0) > 0
          ? new Date(Number(vipItem!.expireTs) * 1000).toISOString()
          : null

        this.membershipInfo = {
          ...this.membershipInfo,
          level: isVip ? 'vip' : 'free',
          remainingPages: total,
          permanentPages,
          temporaryPages,
          nearestExpireDate,
          vipExpireDate
        }
      } finally {
        this.membershipLoading = false
        this.membershipLoaded = true
      }
    },

    // ==================== 翻译记录相关 ====================

    /**
     * Phase3：增量拉取翻译记录（占位）
     * - 接口：GET /api/translations/records?lastRecordId=&pageSize=
     */
    async refreshTasksIncremental(lastRecordId: Int64Id = '0', pageSize: number = 20) {
      const { listRecords } = useTranslationApi()
      const { records } = await listRecords(lastRecordId, pageSize)

      // 映射到 TranslationTask（历史记录）
      const mapped: TranslationTask[] = records.map(r => ({
        id: String(r.recordId),
        originalFileName: r.srcFileName,
        fileSize: '-',
        targetLanguage: r.to,
        status:
          r.translateStatus === TranslateStatus.Success
            ? 'completed'
            : r.translateStatus === TranslateStatus.Failed
            ? 'failed'
            : 'translating',
        createdAt: r.createTime,
        downloadUrl: r.downloadUrl
      }))

      // 同步填充记录元信息，供“重新翻译”复用 fileId
      const meta = { ...this.recordMetaById }
      for (const r of records) {
        meta[String(r.recordId)] = {
          srcFileId: r.srcFileId,
          from: r.from,
          to: r.to,
          srcFileName: r.srcFileName
        }
      }
      this.recordMetaById = meta

      // 合并去重（按 id）
      const exist = new Set(this.allTasks.map(t => t.id))
      const merged = [...this.allTasks]
      for (const m of mapped) {
        if (!exist.has(m.id)) merged.push(m)
      }
      this.allTasks = merged
    },


    /**
     * 刷新单条下载链接（若任务已完成）
     * 返回最新的 downloadUrl（可能为空）
     */
    async refreshDownloadUrl(recordId: Int64Id): Promise<string | undefined> {
      const normalizedRecordId = normalizeInt64IdOrThrow(recordId, 'recordId')
      const api = useTranslationApi()
      const r = await api.queryTaskStatus(normalizedRecordId)
      const id = String(normalizedRecordId)
      const idx = this.allTasks.findIndex(t => t.id === id)
      if (idx >= 0) {
        const t = this.allTasks[idx]
        if (t) {
          if (r.isEnd) {
            if (r.status === TranslateStatus.Success) {
              t.status = 'completed'
              t.downloadUrl = r.downloadUrl
            } else {
              t.status = 'failed'
              t.error = t.error || '翻译失败'
            }
          } else {
            t.status = 'translating'
          }
        }
      }
      return r.downloadUrl
    },


    // 切换Tab
    switchTab(tab: 'all' | 'translating' | 'completed' | 'failed') {
      this.currentTab = tab
    },

    // ==================== 统计信息相关 ====================

    async fetchStatistics() {
      this.statisticsLoading = true
      try {
        // 基于消耗历史轻量推导（若无历史则保持占位不变）
        const src = this.consumptionRecords
        if (!src || src.length === 0) {
          return
        }
        const trans = src.filter(r => r.type === 'docTranslate')
        const now = Date.now()
        const monthly = trans.filter(r => now - Date.parse(r.createdAt) <= 30 * 24 * 3600 * 1000).length
        this.statistics = {
          totalTranslations: trans.length,
          monthlyTranslations: monthly,
          totalFileSize: this.statistics.totalFileSize // 后端暂不提供
        }
      } finally {
        this.statisticsLoading = false
        this.statisticsLoaded = true
      }
    },

    // 从服务端获取统计（优先方案）
    async fetchServerStatistics() {
      this.statisticsLoading = true
      try {
        const { getUserUsageStats } = useTranslationApi()
        const { totalTranslations, monthlyTranslations } = await getUserUsageStats()
        this.statistics = {
          totalTranslations,
          monthlyTranslations,
          totalFileSize: this.statistics.totalFileSize,
        }
      } finally {
        this.statisticsLoading = false
        this.statisticsLoaded = true
      }
    },

    // ==================== 消耗历史相关 ====================

    async fetchConsumptionRecords() {
      const { listQuotaHistory } = useTranslationApi()
      const { items } = await listQuotaHistory('', 50)
      this.consumptionRecords = items.map(it => ({
        id: it.trxId,
        type:
          it.trxType === 'doc_translate'
            ? 'docTranslate'
            : it.trxType === 'doc_translate_fail'
            ? 'docTranslateFail'
            : it.trxType === 'vip_increase'
            ? 'vipIncrease'
            : 'adminOpt',
        fileName: it.fileName,
        increaseTotalPages: it.increaseTotalPages,
        createdAt: it.updateTime,
        metadata: { expireTime: it.expireTime || undefined, reason: it.reason }
      }))
    },

    // ==================== 套餐历史相关（暂不实现，待需求明确再接） ====================
  }
})
